import React, { useState, useCallback, useContext } from 'react';
import { node } from 'prop-types';

import { kycStorage } from '@ajaibid/utils';
import { AlertContext } from '@context/alert';
import usePersistState from '@hooks/usePersistState';

import { getCity, postSubmitKyc } from '@screens/Kyc/fetcher/verificationService';
import { getBankLists } from '@screens/Kyc/fetcher/kycBankAccount';
import useEventTracker from '@screens/Kyc/tracker';

export const KycDukcapilContext = React.createContext();

const KycDukcapilProvider = ({ children }) => {
  const { showAlert } = useContext(AlertContext);

  const [collectDataProfile, setCollectDataProfile] = usePersistState('kyc_collectDataProfile', {}); // useState({});
  const [collectVideo, setCollectVideo] = usePersistState('kyc_collectVideo', null);
  const [collectExperience, setCollectExperience] = usePersistState('kyc_collectExperience', {});
  const [collectOfficeInfo, setCollectOfficeInfo] = usePersistState('kyc_collectOfficeInfo', {});
  const [collectBank, setCollectBank] = usePersistState('kyc_collectBank', {});
  const [collectBo, setCollectBo] = usePersistState('kyc_collectBo', '');

  const [nextEddQuestions, setNextEddQuestions] = usePersistState('kyc_nextEddQuestions', 0);
  const [nextQuestions, setNextQuestions] = usePersistState('kyc_nextQuestions', 1);

  const [sign, setSign] = usePersistState('kyc_sign', '');
  const [imgSignature, setImgSignature] = usePersistState('kyc_sign', '');

  const [selectedPlaceBirth, setSelectedPlaceBirth] = usePersistState('kyc_selectedPlaceBirth', '');
  const [selectedCity, setSelectedCity] = usePersistState('kyc_selectedCity', '');
  const [idCard, setIdCard] = usePersistState('kyc_idCard', '');
  const [manualForm, setManualForm] = usePersistState('kyc_manualForm', true);
  const [automateForm, setAutomateForm] = usePersistState('kyc_automateForm', false);
  const [selectedBank, setSelectedBank] = usePersistState('kyc_selectedBank', '');

  const [kycUserId, setKycUserId] = usePersistState('kyc_kycUserEmail', '');

  const [cityData, setCityData] = useState([]);
  const [bankLists, setBankLists] = useState([]);
  const [submitKycLoading, setSubmitKycLoading] = useState(false);
  const [submitKycResponse, setSubmitKycResponse] = useState(null);

  const { eventKycSixthPageBca, eventKycSixthPagePermata } = useEventTracker();

  const resetData = () => {
    setCollectDataProfile({});
    setCollectVideo(null);
    setCollectExperience({});
    setCollectOfficeInfo({});
    setCollectBank({});
    setCollectBo({});
    setNextEddQuestions(0);
    setNextQuestions(1);
    setImgSignature('');
    setSign('');
    setSelectedPlaceBirth('');
    setSelectedCity('');
    setIdCard('');
    setSelectedBank('');
  };

  const onDoCollectProfileData = (data) => {
    setCollectDataProfile({ ...data, dob: data.date_of_birth });
  };

  const fetchCity = useCallback(() => {
    getCity()
      .then((response) => {
        setCityData(response?.kota);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [getCity]);

  const fetchBankList = useCallback(() => {
    getBankLists()
      .then((response) => {
        if (response.ok && response.json.results) {
          setBankLists(response.json.results);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }, [getBankLists]);

  const submitKyc = useCallback((payload) => {
    const pay = {
      account_number: payload.accountNumber,
      account_owner_name: payload.accountName,
      annual_income: parseInt(payload.annual_income, 10),
      bank_swift_code: payload.bank_swift_code,
      coin_opening: payload.coinOpening,
      dob: payload.dob,
      education_level: payload.education,
      first_name: typeof payload.first_name !== 'undefined' ? payload.first_name : payload.name,
      id_number: typeof payload.id_number !== 'undefined' ? payload.id_number : payload.nik,
      investing_knowledge: parseInt(payload.investing_knowledge, 10),
      investment_objective: parseInt(payload.investment_objective, 10),
      ktp_address: payload.address,
      ktp_photo: idCard.replace('data:image/jpeg;base64,', ''),
      source_income: parseInt(payload.source_income, 10),
      is_dukcapil: payload.automateForm ? 1 : 0,
      occupation:
        payload.occupation && parseInt(payload.occupation, 10) !== 0
          ? parseInt(payload.occupation, 10)
          : 8,
      religion: payload.religion,
      marital_status: payload.marital_status,
      risk_tolerance: payload.risk_tolerance ? payload.risk_tolerance : null,
      company_name: payload.company_name,
      office_address: payload.office_address,
      office_rt: parseInt(payload.office_rt, 10) ? parseInt(payload.office_rt, 10) : 2,
      office_rw: parseInt(payload.office_rw, 10) ? parseInt(payload.office_rw, 10) : 2,
      office_zipcode: payload.office_zipcode,
      office_telp: payload.office_telp,
      mother_maiden_name:
        typeof payload.mother_maiden_name !== 'undefined'
          ? payload.mother_maiden_name
          : payload.motherName,
      section: kycStorage.get('kycKategory') ? kycStorage.get('kycKategory') : 3,
      economy_sector: payload.economy_sector,
      selected_economy_sector: payload.selected_economy_sector,
      video: payload.rescVideo?.toString().replace(/^data:(.*,)?/, '') || '',
      place_birth_code: payload.selectedPlaceBirth ? payload.selectedPlaceBirth.value : null,
      place_birth: payload.selectedPlaceBirth ? payload.selectedPlaceBirth.text : null,
      npwp_number: payload.npwp_number,
    };
    if (pay.npwp_number === null) {
      delete pay.npwp_number;
    }

    if (payload.id_bank === 3 && kycStorage.get('config') === '1') {
      if (kycStorage.get('config') === '1' || kycStorage.get('permataRdn')) {
        pay.my_signature = payload.rescSign.replace('data:image/png;base64,', '');
      } else {
        pay.my_signature = payload.imgSignature.replace('data:image/png;base64,', '');
      }
      pay.rdn_type = 'BCA';
    } else {
      pay.my_signature = payload.rescSign.replace('data:image/png;base64,', '');
    }

    if (payload.economy_sector === 11 || payload.economy_sector === '11') {
      pay.economy_sector_other = payload.economy_sector_other || payload.selected_economy_sector;
    }

    if (payload.manualForm) {
      pay.zip = payload.zipCode;
      pay.ktp_city_code = payload.selectedCity.value;
    }

    if (payload.automateForm) {
      pay.kecamatan = payload.kecamatan;
      pay.kelurahan = payload.kelurahan;
      pay.ktp_city_code = payload.ktp_city_code;
    }
    setSubmitKycLoading(true);

    const wrappedPayload = {
      type: 'PREDEFINED',
      id: 'STOCK',
      data: pay,
    };
    postSubmitKyc(wrappedPayload)
      .then((resp) => {
        if (resp.ok) {
          setSubmitKycResponse(resp.json);
          setSubmitKycLoading(false);
          kycStorage.set('instantKycSubmitted', true);
          kycStorage.remove('haveNpwp');
          kycStorage.remove('kycKategory');

          const eventProps = { 'is successfull': true, error_type: '' };
          if (pay.rdn_type === 'BCA') {
            eventKycSixthPageBca(eventProps);
          } else {
            eventKycSixthPagePermata(eventProps);
          }
        } else {
          showAlert(resp.json?.err_message, 'error');
          setSubmitKycLoading(false);

          const eventProps = { 'is successfull': false, error_type: resp.json?.err_message };
          if (pay.rdn_type === 'BCA') {
            eventKycSixthPageBca(eventProps);
          } else {
            eventKycSixthPagePermata(eventProps);
          }
        }
      })
      .catch((err) => {
        showAlert(err, 'error');
        setSubmitKycLoading(false);

        const eventProps = { 'is successfull': false, error_type: err.message };
        if (pay.rdn_type === 'BCA') {
          eventKycSixthPageBca(eventProps);
        } else {
          eventKycSixthPagePermata(eventProps);
        }
      });
  });

  const valueProps = {
    collectDataProfile,
    onDoCollectProfileData,
    setSelectedPlaceBirth,
    selectedPlaceBirth,
    setSelectedCity,
    selectedCity,
    idCard,
    setIdCard,
    setManualForm,
    manualForm,
    automateForm,
    fetchCity,
    cityData,
    fetchBankList,
    bankLists,
    setCollectVideo,
    collectVideo,
    collectExperience,
    setCollectExperience,
    collectOfficeInfo,
    setCollectOfficeInfo,
    collectBank,
    setCollectBank,
    nextEddQuestions,
    setNextEddQuestions,
    nextQuestions,
    setNextQuestions,
    selectedBank,
    setSelectedBank,
    collectBo,
    setCollectBo,
    sign,
    setSign,
    imgSignature,
    setImgSignature,
    submitKyc,
    submitKycLoading,
    submitKycResponse,
    kycUserId,
    setKycUserId,
    resetData,
  };

  return <KycDukcapilContext.Provider value={valueProps}>{children}</KycDukcapilContext.Provider>;
};

KycDukcapilProvider.propTypes = {
  children: node.isRequired,
};

export default KycDukcapilProvider;
